import { Component, Input, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  FormsModule,
  AbstractControl,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormErrorComponent } from '../form-error/form-error.component';

export type InputType =
  | 'text' | 'email' | 'password' | 'number'
  | 'tel' | 'url' | 'search' | 'date'
  | 'datetime-local' | 'month' | 'time';

@Component({
  selector: 'is-input-field',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() type: InputType = 'text';
  @Input() helpText = '';
  @Input() required = false;
  @Input() readonly = false;
  @Input() prefixIcon = '';
  @Input() suffixIcon = '';
  @Input() control: AbstractControl | null = null;

  value = '';
  isDisabled = false;
  showPassword = false;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};
  private controlSub: Subscription | null = null;

  ngOnInit(): void {
    this.syncFromControl();
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }

  /**
   * Sincroniza o valor interno com o AbstractControl passado via @Input().
   * Assina valueChanges para reagir a patchValue/setValue/reset externos.
   */
  private syncFromControl(): void {
    if (!this.control) return;

    // Sincroniza valor inicial do FormControl
    this.value = this.control.value ?? '';

    // Observa mudanças externas no FormControl
    this.controlSub = this.control.valueChanges.subscribe((val) => {
      this.value = val ?? '';
    });
  }

  get inputType(): string {
    if (this.type === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
    return this.type;
  }

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  get inputClasses(): Record<string, boolean> {
    return {
      'form-control': true,
      'is-invalid': this.hasError,
      'ps-5': !!this.prefixIcon,
    };
  }

  writeValue(val: string): void {
    this.value = val ?? '';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  handleInput(event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    this.value = val;
    this.onChange(val);
  }

  handleBlur(): void {
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
